// ============================================================================
// TeamsHealthCard — « Santé des équipes » (rail accueil E1a)
// ============================================================================
// Lignes équipe + directeur + StatusBadge : score X/5 (répondu) = vert « ok »,
// « En attente » = amber — conforme au mock figé. En-tête droit = résumé
// discret « répondues/total · humeur/5 ». Score AGRÉGÉ d'équipe seulement
// (jamais les textes nominatifs ni MemberPulseSurveys — PII orange).
// Capability-aware : carte masquée sur 403 (employee) par AccueilView.

import { StatusBadge, SkeletonCard, QueryError, EmptyState } from '@gs/ui';
import { HeartPulse } from 'lucide-react';
import type { TFunction } from 'i18next';
import type { TeamsHealth } from '../../hooks/useAccueil';
import { formatMood } from './format';
import { RailCard } from './RailCard';

export interface TeamsHealthCardProps {
  data: TeamsHealth | undefined;
  isLoading: boolean;
  isError: boolean;
  migrationPending: boolean;
  onRetry: () => void;
  locale: string;
  t: TFunction;
}

export function TeamsHealthCard({
  data, isLoading, isError, migrationPending, onRetry, locale, t,
}: TeamsHealthCardProps) {
  if (isLoading) return <SkeletonCard lines={5} />;

  const teams = data?.teams ?? [];

  return (
    <RailCard
      title={t('gest:accueil.teams.title')}
      trailing={
        data && data.total > 0 ? (
          <span className="text-meta font-medium text-slate-400 dark:text-slate-500">
            {t('gest:accueil.teams.summary', {
              answered: data.answered,
              total: data.total,
              mood: data.mood != null ? formatMood(data.mood, locale) : '—',
            })}
          </span>
        ) : undefined
      }
    >
      {migrationPending ? (
        <p className="text-field text-slate-400 dark:text-slate-500">{t('gest:accueil.migrationPending')}</p>
      ) : isError ? (
        <QueryError message={t('gest:accueil.teams.error')} onRetry={onRetry} />
      ) : teams.length === 0 ? (
        <EmptyState
          title={t('gest:accueil.teams.emptyTitle')}
          description={t('gest:accueil.teams.empty')}
          icon={<HeartPulse size={32} className="text-slate-300 dark:text-slate-600" aria-hidden />}
        />
      ) : (
        <ul>
          {teams.map((team) => (
            <li
              key={team.id}
              className="flex items-center justify-between gap-3 border-b border-slate-100 py-2 last:border-b-0 dark:border-slate-700/60"
            >
              <div className="min-w-0">
                <p className="truncate text-field font-medium text-slate-700 dark:text-slate-200">{team.name}</p>
                {team.directorName && (
                  <p className="truncate text-meta text-slate-400 dark:text-slate-500">
                    {t('gest:accueil.teams.director', { name: team.directorName })}
                  </p>
                )}
              </div>
              {team.moodScore != null ? (
                <StatusBadge variant="success">
                  {t('gest:accueil.teams.scoreOutOf5', { score: formatMood(team.moodScore, locale) })}
                </StatusBadge>
              ) : (
                <StatusBadge variant="warning">{t('gest:accueil.teams.pending')}</StatusBadge>
              )}
            </li>
          ))}
        </ul>
      )}
    </RailCard>
  );
}
